// Service Register — printers, photocopiers, toner refilling, cartridges,
// consumables (G.O.(Rt) No. 717/2026/LSGD, Date: 12.03.2026)
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  useRegisterList, RegisterHeader, SearchBox, RegisterPrintActions,
  RegisterTable, RegisterEntryDialog, AddEntryButton, GovFooterNote, fetchBundle, useLocalBodyName,
} from "./shared";
import { sectionsForOption } from "@/lib/registerPdf";

const EMPTY = {
  hardware_id: "", equipment_type: "", make: "", model: "", serial_number: "",
  service_date: "", service_provider: "", service_details: "", consumable_used: "",
  quantity: "", cost: "", technician: "", next_service_date: "", remarks: "",
};

const FIELDS = [
  { name: "hardware_id", label: "Linked Hardware Asset", type: "asset", full: true },
  { name: "equipment_type", label: "Equipment Type", type: "select", options: ["Printer", "Photocopier", "Toner Refilling", "Cartridge Replacement", "Other Consumables", "Computer", "Other"] },
  { name: "service_date", label: "Service Date", type: "date" },
  { name: "make", label: "Make" },
  { name: "model", label: "Model" },
  { name: "serial_number", label: "Serial Number" },
  { name: "service_provider", label: "Service Provider" },
  { name: "technician", label: "Technician" },
  { name: "consumable_used", label: "Consumable Used" },
  { name: "quantity", label: "Quantity" },
  { name: "cost", label: "Cost" },
  { name: "next_service_date", label: "Next Service Date", type: "date" },
  { name: "service_details", label: "Service Details", type: "textarea" },
  { name: "remarks", label: "Remarks", type: "textarea" },
];

export default function ServiceRegisterTab() {
  const { rows, loading, save, remove } = useRegisterList("service");
  const localBody = useLocalBodyName();
  const [assets, setAssets] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.get("/hardware").then(r => setAssets(r.data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r => [r.asset_id, r.equipment_type, r.service_provider, r.service_details, r.consumable_used, r.technician]
      .some(v => (v || "").toLowerCase().includes(q)));
  }, [rows, search]);

  return (
    <div className="space-y-4" data-testid="service-register-tab">
      <RegisterHeader
        title="SERVICE REGISTER"
        formatNo="G.O.(Rt) 717/2026/LSGD"
        localBody={localBody}
        subtitle="Printers, photocopiers, toner refilling, cartridge replacement and other consumables"
        testid="service-register"
      >
        <SearchBox value={search} onChange={setSearch} placeholder="Search equipment, provider, technician…" testid="service-search" />
        <RegisterPrintActions sections={sectionsForOption("service")} getBundle={fetchBundle} filename="Service_Register.pdf" testid="service-print" />
        <AddEntryButton onClick={() => setEditing({ ...EMPTY })} label="Add Service" testid="service-add-btn" />
      </RegisterHeader>

      <RegisterTable
        testid="service-table"
        loading={loading}
        rows={filtered}
        emptyText="No services recorded yet."
        columns={[
          { key: "sl_no", label: "Sl.", render: (r, i) => r.sl_no || i + 1 },
          { key: "equipment_type", label: "Equipment" },
          { key: "asset_id", label: "Asset ID", render: r => <span className="font-mono text-xs">{r.asset_id || "—"}</span> },
          { key: "service_date", label: "Service Date" },
          { key: "service_provider", label: "Provider" },
          { key: "service_details", label: "Details" },
          { key: "consumable_used", label: "Consumable" },
          { key: "cost", label: "Cost" },
          { key: "next_service_date", label: "Next Service" },
        ]}
        onEdit={r => setEditing({ ...EMPTY, ...r })}
        onDelete={async r => { if (window.confirm("Delete this service entry?")) await remove(r); }}
      />
      <GovFooterNote />

      <RegisterEntryDialog
        open={!!editing}
        title={editing?.id ? "Edit Service Entry" : "Add Service Entry"}
        description="Covers printers, photocopiers, toner refilling, cartridge replacement and other consumables."
        fields={FIELDS}
        values={editing || EMPTY}
        onChange={setEditing}
        onClose={() => setEditing(null)}
        onSave={async () => { if (await save(editing, editing?.id ? editing : null)) setEditing(null); }}
        assets={assets}
        testid="service-dialog"
      />
    </div>
  );
}
